/**
 * Service Cluster panel — the persistent, service-shaped keyword pipeline.
 *
 * Replaces the per-seed "Completed Sessions" list. A cluster is a SERVICE the
 * client sells; keywords accumulate under it across every research run.
 *
 * Colour is load-bearing here and follows the repo rule that red/green are
 * reserved for status:
 *   green  — approved, available to write about now
 *   red    — used, permanently blacklisted (an article exists)
 *   amber  — suggested, waiting on client review
 *
 * Metrics render as an em dash when unknown. Never show 0 volume / 999
 * difficulty for a failed lookup — that is the bug this redesign fixes.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Layers,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  useAddClusterKeywords,
  useBulkDeleteClusters,
  useBulkSuggestClusters,
  useClusterExhaustion,
  useCreateCluster,
  useDeleteCluster,
  useServiceCluster,
  useServiceClusters,
  useSeedClustersFromBrandBible,
  useSetKeywordStatus,
  useSuggestClusterKeywords,
} from '@/hooks/useServiceClusters';
import {
  availableCount,
  formatMetric,
  usedCount,
  type ServiceCluster,
  type ServiceClusterKeyword,
} from '@/services/serviceClusterService';

interface ServiceClusterPanelProps {
  tenantId: string;
}

// ---------------------------------------------------------------------------
// Keyword row
// ---------------------------------------------------------------------------

const STATUS_STYLES: Record<string, { dot: string; label: string; pill: string }> = {
  approved: {
    dot: 'bg-[hsl(var(--status-success))]',
    label: 'Available',
    pill: 'bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))]',
  },
  used: {
    dot: 'bg-destructive',
    label: 'Used',
    pill: 'bg-destructive/10 text-destructive',
  },
  queued: {
    dot: 'bg-destructive/60',
    label: 'Queued',
    pill: 'bg-destructive/5 text-destructive',
  },
  suggested: {
    dot: 'bg-[hsl(var(--status-warning))]',
    label: 'Needs review',
    pill: 'bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))]',
  },
  rejected: {
    dot: 'bg-muted-foreground/40',
    label: 'Rejected',
    pill: 'bg-muted text-muted-foreground',
  },
};

function KeywordRow({
  keyword,
  selected,
  onToggleSelect,
  onUse,
}: {
  keyword: ServiceClusterKeyword;
  selected: boolean;
  onToggleSelect: (id: string) => void;
  onUse: (keyword: string) => void;
}) {
  const style = STATUS_STYLES[keyword.status] ?? STATUS_STYLES.suggested;
  const isSpent = keyword.status === 'used' || keyword.status === 'queued';
  const metricsUnknown = keyword.metrics_status !== 'ok';

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md border text-sm transition-colors',
        isSpent ? 'bg-muted/40 border-transparent' : 'bg-card hover:bg-muted/40 cursor-pointer',
        selected && 'ring-1 ring-ring',
      )}
      // The whole row toggles selection (the checkbox alone wasn't discoverable).
      onClick={isSpent ? undefined : () => onToggleSelect(keyword.id)}
    >
      {/* Anything not yet spent is selectable, so an accidental approval can be
          walked back. Used/queued rows are tied to a real article and are not
          re-openable from here — reserve the slot so rows stay aligned. */}
      {isSpent ? (
        <span className="w-[13px] shrink-0" aria-hidden />
      ) : (
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onToggleSelect(keyword.id)}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0 accent-[hsl(var(--status-success))]"
          aria-label={`Select ${keyword.keyword}`}
        />
      )}

      <span className={cn('h-2 w-2 rounded-full shrink-0', style.dot)} aria-hidden />

      <span
        className={cn(
          'flex-1 min-w-0 truncate',
          isSpent && 'line-through text-muted-foreground',
        )}
        title={keyword.rationale ?? undefined}
      >
        {keyword.keyword}
      </span>

      <span
        className="hidden sm:inline text-xs text-muted-foreground tabular-nums w-20 text-right shrink-0"
        title={
          metricsUnknown
            ? keyword.metrics_error ?? 'Search volume not available yet'
            : 'Monthly search volume'
        }
      >
        {formatMetric(keyword.search_volume)} vol
      </span>
      <span
        className="hidden md:inline text-xs text-muted-foreground tabular-nums w-16 text-right shrink-0"
        title={
          metricsUnknown
            ? keyword.metrics_error ?? 'Difficulty not available yet'
            : 'Keyword difficulty'
        }
      >
        KD {formatMetric(keyword.keyword_difficulty)}
      </span>

      <span className={cn('text-xs px-2 py-0.5 rounded-full shrink-0', style.pill)}>
        {style.label}
      </span>

      {keyword.status === 'approved' && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onUse(keyword.keyword);
          }}
        >
          Write
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cluster card
// ---------------------------------------------------------------------------

function ClusterCard({
  cluster,
  tenantId,
  expanded,
  onToggle,
  selected,
  onToggleSelectCluster,
  bulkSuggesting = false,
}: {
  cluster: ServiceCluster;
  tenantId: string;
  expanded: boolean;
  onToggle: () => void;
  selected: boolean;
  onToggleSelectCluster: () => void;
  /** True while a panel-level bulk AI Suggest is running for this cluster. */
  bulkSuggesting?: boolean;
}) {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [manualKeyword, setManualKeyword] = useState('');

  // Keywords only load once a cluster is opened — a client can have dozens.
  const { data: detail, isLoading } = useServiceCluster(tenantId, expanded ? cluster.id : null);
  const suggest = useSuggestClusterKeywords(tenantId);
  const setStatus = useSetKeywordStatus(tenantId);
  const addKeywords = useAddClusterKeywords(tenantId);
  const deleteCluster = useDeleteCluster(tenantId);

  const available = availableCount(cluster.counts);
  const used = usedCount(cluster.counts);
  const pending = cluster.counts.suggested;
  const isExhausted = cluster.status === 'exhausted';

  // What the selection is made of decides which actions the bar offers.
  const selectedRows = (detail?.keywords ?? []).filter((k) =>
    selectedIds.includes(k.id),
  );
  const hasApprovedSelected = selectedRows.some((k) => k.status === 'approved');
  const hasUnapprovedSelected = selectedRows.some((k) => k.status !== 'approved');

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // Select-all covers every keyword that can still change status (not used/queued).
  const selectableIds = (detail?.keywords ?? [])
    .filter((k) => k.status !== 'used' && k.status !== 'queued')
    .map((k) => k.id);
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : selectableIds);

  const handleSuggest = useCallback(async () => {
    try {
      const result = await suggest.mutateAsync({ clusterId: cluster.id, limit: 25 });
      const parts = [`Added ${result.created.length} keyword${result.created.length === 1 ? '' : 's'}`];
      if (result.duplicates_skipped.length) {
        parts.push(`${result.duplicates_skipped.length} already covered`);
      }
      if (result.metrics_unavailable) {
        parts.push(`${result.metrics_unavailable} without metrics`);
      }
      toast.success(parts.join(' · '));
    } catch {
      toast.error('Could not generate keywords. Try again in a moment.');
    }
  }, [suggest, cluster.id]);

  const handleReview = useCallback(
    async (status: 'approved' | 'rejected' | 'suggested') => {
      if (!selectedIds.length) return;
      // Rejecting? Capture an optional reason — it feeds AI Suggest's negative
      // examples so the model stops re-suggesting the same kind of keyword.
      // Cancel on the prompt aborts the reject entirely; OK (even blank) proceeds.
      let reason: string | null = null;
      if (status === 'rejected') {
        const input = window.prompt(
          `Why reject ${selectedIds.length} keyword${selectedIds.length === 1 ? '' : 's'}? (optional — helps AI Suggest improve)`,
        );
        if (input === null) return;
        reason = input.trim() || null;
      }
      // "suggested" is the undo: it pulls a keyword back out of the pipeline
      // without rejecting it outright. The backend also clears any article link.
      const VERB: Record<typeof status, string> = {
        approved: 'approved',
        rejected: 'rejected',
        suggested: 'moved back to review',
      };
      try {
        await setStatus.mutateAsync({
          clusterId: cluster.id,
          keywordIds: selectedIds,
          status,
          reason,
        });
        toast.success(
          `${selectedIds.length} keyword${selectedIds.length === 1 ? '' : 's'} ${VERB[status]}`,
        );
        setSelectedIds([]);
      } catch {
        toast.error('Could not update those keywords.');
      }
    },
    [selectedIds, setStatus, cluster.id],
  );

  const handleAddManual = useCallback(async () => {
    const value = manualKeyword.trim();
    if (!value) return;
    try {
      const result = await addKeywords.mutateAsync({
        clusterId: cluster.id,
        keywords: [value],
        status: 'approved',
      });
      if (result.created.length) {
        toast.success(`Added "${value}"`);
        setManualKeyword('');
      } else {
        toast('That keyword is already in this client\'s pipeline.');
      }
    } catch {
      toast.error('Could not add that keyword.');
    }
  }, [manualKeyword, addKeywords, cluster.id]);

  // The connection the redesign session found confusing: a keyword goes
  // straight into Quick Create with the cluster carried along.
  const handleUse = useCallback(
    (keyword: string) => {
      navigate(
        `/content/planner?keyword=${encodeURIComponent(keyword)}&cluster=${encodeURIComponent(cluster.name)}`,
      );
    },
    [navigate, cluster.name],
  );

  const handleDelete = useCallback(async () => {
    if (
      !window.confirm(
        `Delete "${cluster.name}"? Its ${used} used keyword${used === 1 ? '' : 's'} will no longer be blocked from being written again.`,
      )
    ) {
      return;
    }
    try {
      await deleteCluster.mutateAsync(cluster.id);
      toast.success(`Deleted "${cluster.name}"`);
    } catch {
      toast.error('Could not delete that cluster.');
    }
  }, [deleteCluster, cluster.id, cluster.name, used]);

  return (
    <Card className={cn('border shadow-sm overflow-hidden', isExhausted && 'border-destructive/30', selected && 'ring-1 ring-teal')}>
      <div className="w-full flex items-center gap-3 px-5 py-4 bg-muted hover:bg-muted/80 transition-colors">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggleSelectCluster}
          className="shrink-0 accent-teal"
          aria-label={`Select cluster ${cluster.name}`}
        />
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center justify-between text-left min-w-0"
        >
        <div className="flex items-center gap-3 min-w-0">
          <Layers className="h-4 w-4 shrink-0 text-teal" />
          <span className="font-semibold text-sm truncate">{cluster.name}</span>
          {cluster.source === 'brand_bible' && (
            <Badge variant="outline" className="hidden sm:inline-flex text-[10px] shrink-0">
              Brand Bible
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--status-success-bg))] text-[hsl(var(--status-success))] font-medium"
            title="Approved keywords available to write about"
          >
            {available} available
          </span>
          {used > 0 && (
            <span
              className="text-xs px-2 py-0.5 rounded-full bg-destructive/10 text-destructive font-medium"
              title="Keywords already written about — blocked from reuse"
            >
              {used} used
            </span>
          )}
          {pending > 0 && (
            <span
              className="text-xs px-2 py-0.5 rounded-full bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))] font-medium"
              title="Suggested keywords waiting for client review"
            >
              {pending} needs review
            </span>
          )}
          {bulkSuggesting && (
            <RefreshCw
              className="h-3.5 w-3.5 text-coral animate-spin"
              aria-label="Generating keywords…"
            />
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              expanded && 'rotate-180',
            )}
          />
        </div>
        </button>
      </div>

      {expanded && (
        <div className="border-t bg-card">
          <div className="px-5 py-3 flex flex-wrap items-center gap-2 border-b bg-muted/50">
            <Button
              size="sm"
              onClick={handleSuggest}
              disabled={suggest.isPending || bulkSuggesting}
              className="h-7 px-3 text-xs bg-coral hover:bg-coral-hover text-white"
            >
              {suggest.isPending || bulkSuggesting ? (
                <RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="h-3 w-3 mr-1.5" />
              )}
              AI Suggest
            </Button>

            <div className="flex items-center gap-1.5">
              <Input
                value={manualKeyword}
                onChange={(e) => setManualKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddManual();
                  }
                }}
                placeholder="Add a keyword yourself…"
                className="h-7 text-xs w-52"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={handleAddManual}
                disabled={!manualKeyword.trim() || addKeywords.isPending}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>

            {selectableIds.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-2 text-xs"
                onClick={toggleSelectAll}
              >
                {allSelected ? 'Clear selection' : 'Select all'}
              </Button>
            )}

            <div className="flex-1" />

            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-destructive"
              onClick={handleDelete}
              disabled={deleteCluster.isPending}
              title="Delete this cluster"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>

          {selectedIds.length > 0 && (
            <div className="px-5 py-2 flex items-center gap-2 border-b bg-[hsl(var(--status-warning-bg))]">
              <span className="text-xs font-medium">
                {selectedIds.length} selected
              </span>
              <div className="flex-1" />
              {/* Offer only the transitions the selection can actually make, so
                  the bar never shows "Approve" for already-approved keywords. */}
              {hasUnapprovedSelected && (
                <Button
                  size="sm"
                  className="h-7 px-3 text-xs bg-[hsl(var(--status-success))] hover:bg-[hsl(var(--status-success)/0.9)] text-white"
                  onClick={() => handleReview('approved')}
                  disabled={setStatus.isPending}
                >
                  <Check className="h-3 w-3 mr-1" />
                  Approve
                </Button>
              )}
              {hasApprovedSelected && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-3 text-xs"
                  onClick={() => handleReview('suggested')}
                  disabled={setStatus.isPending}
                  title="Take these back out of the pipeline, without rejecting them"
                >
                  <Undo2 className="h-3 w-3 mr-1" />
                  Back to review
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-3 text-xs"
                onClick={() => handleReview('rejected')}
                disabled={setStatus.isPending}
              >
                <X className="h-3 w-3 mr-1" />
                Reject
              </Button>
            </div>
          )}

          <ScrollArea className="max-h-[420px]">
            <div className="p-4 space-y-1.5">
              {isLoading && (
                <p className="text-xs text-muted-foreground py-4 text-center">
                  Loading keywords…
                </p>
              )}

              {!isLoading && (detail?.keywords ?? []).length === 0 && (
                <div className="py-8 text-center space-y-2">
                  <p className="text-sm text-muted-foreground">
                    No keywords in this cluster yet.
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Run AI Suggest to fill it, then review with the client.
                  </p>
                </div>
              )}

              {/* Rows are status-sorted (approved first), so the section label
                  has to be emitted inline at the first suggested row — a header
                  above the whole list would sit over the approved ones and read
                  as if they still needed review. */}
              {(detail?.keywords ?? []).map((keyword, index, rows) => {
                const isFirstSuggested =
                  keyword.status === 'suggested' &&
                  rows.findIndex((k) => k.status === 'suggested') === index;
                return (
                  <React.Fragment key={keyword.id}>
                    {isFirstSuggested && (
                      <p className="text-[11px] uppercase tracking-wide text-muted-foreground pt-2 pb-0.5">
                        Waiting for review
                      </p>
                    )}
                    <KeywordRow
                      keyword={keyword}
                      selected={selectedIds.includes(keyword.id)}
                      onToggleSelect={toggleSelect}
                      onUse={handleUse}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export function ServiceClusterPanel({ tenantId }: ServiceClusterPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [newClusterName, setNewClusterName] = useState('');
  const [selectedClusterIds, setSelectedClusterIds] = useState<string[]>([]);
  const [seedWithKeywords, setSeedWithKeywords] = useState(false);

  const { data, isLoading } = useServiceClusters(tenantId);
  const { data: exhaustion } = useClusterExhaustion(tenantId);
  const seed = useSeedClustersFromBrandBible(tenantId);
  const createCluster = useCreateCluster(tenantId);
  const bulkDelete = useBulkDeleteClusters(tenantId);
  const bulkSuggest = useBulkSuggestClusters(tenantId);

  const clusters = data?.clusters ?? [];

  const toggleSelectCluster = useCallback((id: string) => {
    setSelectedClusterIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);

  // Select-all / deselect-all for the whole cluster list.
  const allClusterIds = clusters.map((c) => c.id);
  const allClustersSelected =
    allClusterIds.length > 0 && allClusterIds.every((id) => selectedClusterIds.includes(id));
  const toggleSelectAllClusters = () =>
    setSelectedClusterIds(allClustersSelected ? [] : allClusterIds);

  // Run AI Suggest across many clusters + summarise. Reused by the bulk bar and
  // by "create + generate".
  const runBulkSuggest = useCallback(
    async (clusterIds: string[]) => {
      const { results } = await bulkSuggest.mutateAsync({ clusterIds });
      const added = results.reduce((n, r) => n + r.created, 0);
      const failed = results.filter((r) => r.error).length;
      toast.success(
        `Generated ${added} keyword${added === 1 ? '' : 's'} across ${results.length} cluster${results.length === 1 ? '' : 's'}` +
          (failed ? ` · ${failed} failed` : ''),
      );
    },
    [bulkSuggest],
  );

  const handleSeed = useCallback(async () => {
    try {
      const result = await seed.mutateAsync();
      if (result.created.length) {
        toast.success(
          `Created ${result.created.length} cluster${result.created.length === 1 ? '' : 's'} from the Brand Bible`,
        );
        if (seedWithKeywords) {
          toast('Generating keywords for the new clusters — this can take a moment…');
          try {
            await runBulkSuggest(result.created.map((c) => c.id));
          } catch {
            toast.error(
              'Clusters created, but keyword generation failed — use AI Suggest per cluster.',
            );
          }
        }
      } else {
        toast('Every service in the Brand Bible already has a cluster.');
      }
    } catch (error: unknown) {
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Could not read services from the Brand Bible.';
      toast.error(detail);
    }
  }, [seed, seedWithKeywords, runBulkSuggest]);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedClusterIds.length) return;
    if (
      !window.confirm(
        `Delete ${selectedClusterIds.length} cluster${selectedClusterIds.length === 1 ? '' : 's'}? Their used-keyword history will no longer block those keywords from being written again.`,
      )
    ) {
      return;
    }
    try {
      const { deleted, failed } = await bulkDelete.mutateAsync(selectedClusterIds);
      toast.success(
        `Deleted ${deleted} cluster${deleted === 1 ? '' : 's'}` +
          (failed.length ? ` · ${failed.length} failed` : ''),
      );
      setSelectedClusterIds([]);
    } catch {
      toast.error('Could not delete those clusters.');
    }
  }, [selectedClusterIds, bulkDelete]);

  const handleBulkSuggest = useCallback(async () => {
    if (!selectedClusterIds.length) return;
    try {
      await runBulkSuggest(selectedClusterIds);
    } catch {
      toast.error('Could not generate keywords for those clusters.');
    }
  }, [selectedClusterIds, runBulkSuggest]);

  const handleCreate = useCallback(async () => {
    const name = newClusterName.trim();
    if (!name) return;
    try {
      await createCluster.mutateAsync({ name });
      toast.success(`Created "${name}"`);
      setNewClusterName('');
    } catch (error: unknown) {
      const detail =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Could not create that cluster.';
      toast.error(detail);
    }
  }, [newClusterName, createCluster]);

  if (isLoading) {
    return (
      <p className="text-xs text-muted-foreground py-6 text-center">Loading clusters…</p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 px-0.5">
        <div className="flex items-center gap-3">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Service Clusters
          </h2>
          {clusters.length > 0 && (
            <button
              type="button"
              onClick={toggleSelectAllClusters}
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2"
            >
              {allClustersSelected ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>
        <span className="text-xs text-muted-foreground">
          {exhaustion?.total_approved_remaining ?? 0} keyword
          {(exhaustion?.total_approved_remaining ?? 0) === 1 ? '' : 's'} ready to write
        </span>
      </div>

      {/* Exhaustion prompt — the "we can no longer generate blogs" notice. */}
      {exhaustion && exhaustion.exhausted.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">
              {exhaustion.exhausted.length} cluster
              {exhaustion.exhausted.length === 1 ? ' has' : 's have'} run out of keywords
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {exhaustion.exhausted.map((c) => c.name).join(', ')} — open a cluster and
              run AI Suggest to add more. Used keywords are never repeated.
            </p>
          </div>
        </div>
      )}

      {exhaustion && exhaustion.exhausted.length === 0 && exhaustion.running_low.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-[hsl(var(--status-warning))]/30 bg-[hsl(var(--status-warning-bg))] px-4 py-3">
          <AlertTriangle className="h-4 w-4 text-[hsl(var(--status-warning))] shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium">Running low</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {exhaustion.running_low
                .map((c) => `${c.name} (${c.approved_remaining} left)`)
                .join(', ')}
            </p>
          </div>
        </div>
      )}

      {/* Bulk action bar — appears once one or more clusters are selected (#1324/#1326). */}
      {selectedClusterIds.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-muted/60 px-4 py-2">
          <span className="text-xs font-medium">
            {selectedClusterIds.length} cluster{selectedClusterIds.length === 1 ? '' : 's'} selected
          </span>
          <div className="flex-1" />
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-3 text-xs"
            onClick={handleBulkSuggest}
            disabled={bulkSuggest.isPending}
          >
            {bulkSuggest.isPending ? (
              <RefreshCw className="h-3 w-3 mr-1.5 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3 mr-1.5" />
            )}
            AI Suggest selected
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-3 text-xs text-destructive hover:text-destructive"
            onClick={handleBulkDelete}
            disabled={bulkDelete.isPending}
          >
            <Trash2 className="h-3 w-3 mr-1.5" />
            Delete selected
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setSelectedClusterIds([])}
          >
            Clear
          </Button>
        </div>
      )}

      {clusters.length === 0 ? (
        <Card className="border shadow-sm">
          <CardContent className="py-12 text-center space-y-3">
            <Layers className="w-10 h-10 text-muted-foreground/30 mx-auto" />
            <p className="text-sm font-medium text-muted-foreground">
              No service clusters yet
            </p>
            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              Clusters are the services this client sells — residential moving, water
              heater replacement, and so on. Keywords live under the service they belong
              to instead of under a one-off search.
            </p>
            <Button
              size="sm"
              onClick={handleSeed}
              disabled={seed.isPending}
              className="bg-coral hover:bg-coral-hover text-white"
            >
              {seed.isPending ? (
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              )}
              Create from Brand Bible services
            </Button>
            <label className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1 cursor-pointer">
              <input
                type="checkbox"
                checked={seedWithKeywords}
                onChange={(e) => setSeedWithKeywords(e.target.checked)}
                className="accent-coral"
              />
              Also generate keywords with AI Suggest (slower)
            </label>
          </CardContent>
        </Card>
      ) : (
        clusters.map((cluster) => (
          <ClusterCard
            key={cluster.id}
            cluster={cluster}
            tenantId={tenantId}
            expanded={expandedId === cluster.id}
            onToggle={() =>
              setExpandedId((prev) => (prev === cluster.id ? null : cluster.id))
            }
            selected={selectedClusterIds.includes(cluster.id)}
            onToggleSelectCluster={() => toggleSelectCluster(cluster.id)}
            bulkSuggesting={bulkSuggest.isPending && selectedClusterIds.includes(cluster.id)}
          />
        ))
      )}

      {/* Add a cluster by hand — services the Brand Bible missed. */}
      <div className="flex items-center gap-2 pt-1">
        <Input
          value={newClusterName}
          onChange={(e) => setNewClusterName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleCreate();
            }
          }}
          placeholder="Add a service cluster…"
          className="h-8 text-sm max-w-xs"
        />
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs"
          onClick={handleCreate}
          disabled={!newClusterName.trim() || createCluster.isPending}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
        {clusters.length > 0 && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 text-xs text-muted-foreground"
            onClick={handleSeed}
            disabled={seed.isPending}
          >
            Sync from Brand Bible
          </Button>
        )}
      </div>
    </div>
  );
}
